import { Dropdown, Upload, message, Menu, Input, Modal, Select, Radio, Form, notification } from "antd";
import Table from "atoms/Table";
import { CALL_API } from "common/API";
import { STATUS_CODE } from "common/Constants";
import React, { useEffect, useRef, useState } from "react";
import { ButtonFilled, ButtonOutlined } from "reusableComponent/UIButtons";
import styled from "styled-components";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import { toggleProcessingModal, toggleSuccessModal } from "redux/actions";
import { getMenuList } from "redux/selectors";
import moment from "moment";
import { SearchOutlined } from "@mui/icons-material";
import { UploadOutlined } from "@ant-design/icons";

const StyledViewMenu = styled.div`
  width: 150px;
  cursor: pointer;
`;
const StyledDropdownMenu = styled(Dropdown)`
  width: 150px;
  cursor: pointer;
`;

// ── same styled modal used across the dashboard ──────────────────────────────
const StyledNewModal = styled(Modal)`
  .ant-modal-close-x {
    display: none;
  }
  .ant-modal-title {
    color: #1f243b !important;
    font-size: 18px;
    font-weight: 500;
    line-height: 23px;
    text-align: left;
  }
  .ant-modal-body {
    padding: 0px 24px 8px 24px;
  }
  .ant-modal-header {
    border-bottom: none !important;
    border-radius: 10px 10px 0 0;
    padding: 20px 24px 12px;
  }
  .ant-modal-content {
    border-radius: 10px;
  }
  .ant-modal-footer {
    border-top: 1px solid #f0f0f0 !important;
    padding: 12px 24px;
  }
`;

// Force a real file-save (not a preview navigation) for the Download button.
// The HTML `download` attribute is only honored by browsers for same-origin
// URLs. Since the S3 signed URL is cross-origin, we fetch the bytes into a
// blob first — a blob: URL is always same-origin to the app, so `download`
// is respected reliably and the browser saves the file instead of opening it.
const triggerDownload = async (url, fileName) => {
  if (!url) return;
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`Fetch failed: ${res.status}`);
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = fileName || "download";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(blobUrl);
  } catch (err) {
    console.error("Download failed:", err);
    message.error("Download failed. Please try again.");
  }
};

function DocumentTableView({
  IdentityInfoColumns,
  dataSource,
  goBack,
  selectedItem,
  fetchMasterData,
  filteruser,
  clientId,
  updateProcessingModal,
  onlyView = false,
}) {
  const [isDocumentUpload, setIsDocumentupload] = useState(false);
  const [fileDetails, setFileDetails] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewType, setPreviewType] = useState("");
  const [previewFileName, setPreviewFileName] = useState("");
  const containerRef = useRef(null);

  // ── CV modal state ──────────────────────────────────────────────────────────
  const [isCvModalOpen, setIsCvModalOpen] = useState(false);
  const [cvType, setCvType] = useState("long");
  const [cvFileType, setCvFileType] = useState(undefined);
  const [cvFileDetails, setCvFileDetails] = useState(null);

  const allowedWordExtensions = [".doc", ".docx"];
  const allowedPptExtensions = [".ppt", ".pptx"];

  const handleOpenCvModal = () => {
    setCvType("long");
    setCvFileType(undefined);
    setCvFileDetails(null);
    setIsCvModalOpen(true);
  };

  const handleCloseCvModal = () => {
    setIsCvModalOpen(false);
    setCvType("long");
    setCvFileType(undefined);
    setCvFileDetails(null);
  };

  const getFormattedDateTime = () => {
    const now = new Date();
    return {
      createdOn: now.toISOString().split("T")[0],
      createdTime: now.toTimeString().split(" ")[0],
    };
  };

  const handleCvUpload = async () => {
    if (!cvFileDetails || !cvType || !cvFileType) {
      notification.error({ message: "Please fill all fields and select a file." });
      return;
    }

    // check for duplicate cv type + file type combo
    const existing = filteruser?.cv || [];
    const isDuplicate = existing.some(
      (item) => item.cvtype === cvType && item.filetype === cvFileType
    );
    if (isDuplicate) {
      notification.error({
        message: "Duplicate File Detected",
        description: `A CV with type "${cvFileType}" and form "${cvType}" already exists.`,
      });
      return;
    }

    updateProcessingModal(true);
    try {
      const res = await CALL_API(
        `upload-identity/${clientId}/${filteruser?.userid}?endpoint=peopledocuments`,
        "post",
        { filename: cvFileDetails.name }
      );

      if (res?.url) {
        const putRes = await fetch(res.url, {
          method: "PUT",
          headers: { "Content-Type": cvFileDetails.type || "application/octet-stream" },
          body: cvFileDetails.filevalues,
        });

        if (putRes.ok) {
          const { createdOn, createdTime } = getFormattedDateTime();
          const { code } = await CALL_API(
            `upload-identity/${clientId}/${filteruser?.userid}`,
            "post",
            {
              name: cvFileDetails.name,
              type: cvFileDetails.type,
              objectKey: res?.key || "",
              documentName: "cv",
              fileSize: cvFileDetails.fileSize,
              filetype: cvFileType,
              cvtype: cvType,
              createdOn,
              createdTime,
            }
          );

          if (code === STATUS_CODE.SUCCESS) {
            await fetchMasterData();
            message.success("CV uploaded successfully");
            handleCloseCvModal();
          } else {
            message.error("You can only upload files up to 10MB in size.");
          }
        } else {
          message.error("File upload failed");
        }
      }
    } catch (err) {
      console.error(err);
      message.error("Upload failed. Please try again.");
    } finally {
      updateProcessingModal(false);
    }
  };
  // ───────────────────────────────────────────────────────────────────────────

  const backStyle = { cursor: "pointer" };

  const otherColumn = [
    {
      title: "File Name",
      dataIndex: "documentName",
      key: "documentName",
      render: (text, record) => (
        <span
          style={{ color: "#0dcaf0", cursor: "pointer" }}
          onClick={(e) => { e.stopPropagation(); handleRowPreview(record); }}
        >
          {text}
        </span>
      ),
    },
    {
      title: "Date added",
      dataIndex: "createdon",
      key: "createdon",
      render: (date) => moment(date).format("DD-MM-YYYY"),
    },
    {
      title: "Size",
      dataIndex: "fileSize",
      key: "fileSize",
      render: (size) => (size ? `${size} kb` : "-"),
    },
    {
      title: "",
      dataIndex: "document",
      key: "document",
      render: (_, record) => (
        <div ref={containerRef}>
          <StyledDropdownMenu
            getPopupContainer={() => containerRef.current}
            overlay={
              <StyledViewMenu>
                <Menu>
                  <Menu.Item key="1" onClick={({ domEvent }) => { domEvent.stopPropagation(); handleRowPreview(record); }}>
                    View
                  </Menu.Item>
                  <Menu.Item key="2" onClick={({ domEvent }) => { domEvent.stopPropagation(); handleMenuClick(record?.documentId, selectedItem?.value, record?.documentName); }}>
                    Download
                  </Menu.Item>
                  {!onlyView && (
                    <Menu.Item key="4" onClick={({ domEvent }) => { domEvent.stopPropagation(); deleteDocument(record?.documentId); }}>
                      Delete
                    </Menu.Item>
                  )}
                </Menu>
              </StyledViewMenu>
            }
            trigger={["click"]}
            placement="bottomRight"
          >
            <span onClick={(e) => { e.stopPropagation(); e.preventDefault(); }}>
              <MoreHorizIcon />
            </span>
          </StyledDropdownMenu>
        </div>
      ),
    },
  ];

  const handleupload = async () => {
    updateProcessingModal(true);
    const res = await CALL_API(
      `upload-identity/${clientId}/${filteruser?.userid}?endpoint=peopledocuments`,
      "post",
      { filename: fileDetails.name }
    );
    if (res?.url) {
      const putRes = await fetch(res.url, {
        method: "PUT",
        headers: { "Content-Type": fileDetails.type || "application/octet-stream" },
        body: fileDetails.filevalues,
      });
      if (putRes.ok) {
        const { code } = await CALL_API(
          `upload-identity/${clientId}/${filteruser?.userid}`,
          "post",
          {
            name: fileDetails.name,
            type: fileDetails.type,
            objectKey: res?.key || "",
            documentName: fileDetails?.documentName,
            fileSize: fileDetails?.fileSize,
          }
        );
        if (code === STATUS_CODE.SUCCESS) {
          await fetchMasterData();
          message.success("Document uploaded");
        } else {
          message.error("You can only upload files up to 10MB in size.");
        }
      } else {
        message.error("File upload failed");
      }
    }
    setFileDetails("");
    updateProcessingModal(false);
    setIsDocumentupload(false);
  };

  useEffect(() => {
    if (fileDetails !== "") handleupload();
  }, [fileDetails]);

  // Revoke any blob URL we created for preview whenever it changes or on unmount
  useEffect(() => {
    return () => {
      if (previewUrl && previewUrl.startsWith("blob:")) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const handleMenuClick = async (id, type, fileName) => {
    try {
      updateProcessingModal(true);
      const { code, url } = await CALL_API(`document-list/${clientId}`, "post", {
        method: "preview-identity-document",
        documentId: id,
        identityName: type,
        userId: filteruser?.userid,
      });
      if (code === STATUS_CODE.SUCCESS) {
        const resolvedURL =
          typeof url === "string"
            ? url
            : url?.url || url?.publicurl || url?.signedUrl || url?.fileUrl || "";
        if (!resolvedURL) {
          message.error("Could not resolve document URL.");
          return;
        }
        // Native anchor download instead of a JS fetch-based helper — avoids
        // CORS-related corrupted/failed downloads on private S3 buckets.
        triggerDownload(resolvedURL, fileName);
      } else {
        message.error("Something went wrong!! Please try again.");
      }
    } catch (error) {
      message.error("Download failed. Please try again.");
    } finally {
      updateProcessingModal(false);
    }
  };

  const deleteDocument = async (id) => {
    updateProcessingModal(true);
    const { code } = await CALL_API(`upload-document/${clientId}/${id}`, "delete", {
      documentName: selectedItem.value,
      userId: filteruser?.userid,
    });
    if (code === STATUS_CODE.SUCCESS) {
      fetchMasterData();
      message.success("Document has been Deleted");
    }
    updateProcessingModal(false);
  };

  const handleSearchChange = (e) => setSearchTerm(e.target.value);

  const handleRowPreview = async (record) => {
    try {
      updateProcessingModal(true);
      const { code, url } = await CALL_API(`document-list/${clientId}`, "post", {
        method: "preview-identity-document",
        documentId: record?.documentId,
        identityName: selectedItem?.value,
        userId: filteruser?.userid,
      });
      if (code === STATUS_CODE.SUCCESS) {
        const resolvedURL =
          typeof url === "string"
            ? url
            : url?.url || url?.publicurl || url?.signedUrl || url?.fileUrl || "";

        if (!resolvedURL) {
          message.error("Could not resolve document URL.");
          return;
        }

        const ext = resolvedURL.split(".").pop().split("?")[0].toLowerCase();
        setPreviewFileName(record?.documentName);

        if (["png", "jpg", "jpeg", "gif"].includes(ext)) {
          setPreviewType("image");
          setPreviewUrl(resolvedURL);
          setPreviewVisible(true);
        } else if (ext === "pdf") {
          // Chrome's embedded PDF viewer needs CORS for its internal range
          // requests when iframed cross-origin. A direct top-level
          // navigation doesn't need CORS at all, so open in a new tab
          // instead of rendering inside the modal's iframe.
          window.open(resolvedURL, "_blank", "noopener,noreferrer");
        } else if (["doc", "docx", "ppt", "pptx", "xls", "xlsx"].includes(ext)) {
          setPreviewType("office");
          setPreviewUrl(resolvedURL);
          setPreviewVisible(true);
        } else {
          setPreviewType("other");
          setPreviewUrl(resolvedURL);
          setPreviewVisible(true);
        }
      } else {
        message.error("Something went wrong!! Please try again.");
      }
    } catch (error) {
      console.error("Preview error:", error);
      message.error("Preview failed. Please try again.");
    } finally {
      updateProcessingModal(false);
    }
  };

  const filteredDataSource = (filteruser?.[selectedItem?.value] || []).filter((item) => {
    const search = searchTerm?.toLowerCase();
    return (
      item?.documentName?.toLowerCase()?.includes(search) ||
      item?.name?.toLowerCase()?.includes(search)
    );
  });

  const updatedDataSource = filteredDataSource?.map((item) => {
    if (item.documentName) {
      const { documentName, ...rest } = item;
      return { ...rest, name: documentName, documentName };
    }
    return item;
  });

  const searchStyle = { margin: "10px 0px", maxWidth: "400px" };
  const isCv = selectedItem?.value === "cv";

  return (
    <div>
      <h6>
        <u style={backStyle} onClick={goBack}>Back</u> {">"} {selectedItem.label}
      </h6>

      <div className="w-100 d-flex justify-content-between">
        {selectedItem.label !== "Identity Information" && (
          <Input
            placeholder="Search"
            onChange={handleSearchChange}
            suffix={<SearchOutlined />}
            style={searchStyle}
          />
        )}

        <div className="text-end">
          {selectedItem.label !== "Identity Information" && !onlyView && (
            // ── CV → open styled modal; everything else → Upload as before ──
            isCv ? (
              <ButtonFilled onClick={handleOpenCvModal}>Add New</ButtonFilled>
            ) : (
              <Upload
                showUploadList={false}
                customRequest={({ file, onSuccess }) => {
                  const reader = new FileReader();
                  reader.readAsDataURL(file);
                  reader.onload = () => {
                    onSuccess();
                    setFileDetails({
                      name: file.name,
                      type: file.type,
                      fileSize: file.size,
                      documentName: selectedItem.value,
                      filevalues: file,
                    });
                  };
                  reader.onerror = (err) => console.error("File read error:", err);
                }}
                onChange={(info) => {
                  if (info.file.status === "done" && info.fileList.length > 0) {
                    const file = info.fileList[0].originFileObj;
                    const reader = new FileReader();
                    reader.onload = () => setIsDocumentupload(false);
                    reader.readAsDataURL(file);
                  }
                }}
              >
                <ButtonFilled loading={isDocumentUpload}>Add New</ButtonFilled>
              </Upload>
            )
          )}
        </div>
      </div>

      <Table
        className="mb-5"
        dataSource={
          selectedItem.label === "Identity Information" ? dataSource : updatedDataSource
        }
        columns={
          selectedItem.label === "Identity Information" || selectedItem.label === "CV"
            ? IdentityInfoColumns
            : otherColumn
        }
        rowClassName={(record, index) => (index % 2 === 0 ? "" : "non-white")}
        rowKey="documentId"
      />

      {/* Preview modal — used for images / office / other. PDFs open in a new tab instead. */}
      <Modal
        visible={previewVisible}
        footer={null}
        width="80%"
        style={{ top: 20 }}
        onCancel={() => {
          if (previewUrl && previewUrl.startsWith("blob:")) {
            URL.revokeObjectURL(previewUrl);
          }
          setPreviewVisible(false);
          setPreviewUrl("");
        }}
      >
        {previewType === "image" && (
          <img src={previewUrl} alt="Preview" style={{ width: "100%", height: "auto" }} />
        )}
        {previewType === "office" && (
          <div style={{ textAlign: "center", padding: "60px 20px" }}>
            <p>Preview isn't available for this file type.</p>
            <ButtonFilled onClick={() => triggerDownload(previewUrl, previewFileName)}>
              Download
            </ButtonFilled>
          </div>
        )}
        {previewType === "other" && (
          <iframe
            src={previewUrl}
            title="Preview"
            style={{ width: "100%", height: "80vh", border: "none" }}
          />
        )}
      </Modal>

      {/* ── CV Add New modal (dashboard style) ─────────────────────────────── */}
      <StyledNewModal
        title="Adding CV"
        open={isCvModalOpen}
        onCancel={handleCloseCvModal}
        footer={[
          <ButtonOutlined key="cancel" onClick={handleCloseCvModal}>
            Cancel
          </ButtonOutlined>,
          <ButtonFilled
            key="save"
            onClick={handleCvUpload}
            disabled={!cvFileDetails || !cvFileType || !cvType}
          >
            Save
          </ButtonFilled>,
        ]}
      >
        <div style={{ paddingTop: 16 }}>
          {/* File Type */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontWeight: 500, display: "block", marginBottom: 6 }}>
              File Type
            </label>
            <Select
              value={cvFileType}
              onChange={(val) => setCvFileType(val)}
              style={{ width: "100%" }}
              placeholder="Select file type"
              options={[
                { label: "Word Document", value: "Word Document" },
                { label: "Power Point Presentation", value: "Power Point Presentation" },
              ]}
            />
          </div>

          {/* CV Type */}
          <div style={{ marginBottom: 20 }}>
            <label style={{ fontWeight: 500, display: "block", marginBottom: 8 }}>
              CV Type
            </label>
            <Radio.Group
              value={cvType}
              onChange={(e) => setCvType(e.target.value)}
              style={{ display: "flex", gap: 20 }}
            >
              <Radio value="long">Long Form</Radio>
              <Radio value="short">Short Form</Radio>
            </Radio.Group>
          </div>

          {/* File Upload */}
          <div>
            <label style={{ fontWeight: 500, display: "block", marginBottom: 6 }}>
              Upload File
            </label>
            <Upload
              accept={
                cvFileType === "Word Document"
                  ? allowedWordExtensions.join(",")
                  : allowedPptExtensions.join(",")
              }
              beforeUpload={(file) => {
                const ext = file.name.substring(file.name.lastIndexOf(".")).toLowerCase();
                const allowed = cvFileType === "Word Document"
                  ? allowedWordExtensions
                  : allowedPptExtensions;

                if (!allowed.includes(ext)) {
                  notification.error({
                    message: "Invalid File Type",
                    description: "Only Word (.doc, .docx) and PowerPoint (.ppt, .pptx) files are allowed.",
                  });
                  return Upload.LIST_IGNORE;
                }

                setCvFileDetails({
                  name: file.name,
                  type: file.type,
                  fileSize: file.size,
                  filevalues: file,
                });
                return false;
              }}
              showUploadList={false}
              disabled={!cvFileType}
            >
              <ButtonFilled
                icon={<UploadOutlined />}
                disabled={!cvFileType}
                style={{ width: "100%", display: "flex", boxSizing: "border-box", margin: 0 }}
              >
                Select File
              </ButtonFilled>
            </Upload>

            {cvFileDetails?.name && (
              <p style={{ marginTop: 8, color: "#636980", fontSize: 13 }}>
                {cvFileDetails.name}
              </p>
            )}
          </div>
        </div>
      </StyledNewModal>
    </div>
  );
}

const mapStateToProps = (state) => ({ menuList: getMenuList(state) });

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    { updateProcessingModal: toggleProcessingModal, updateSuccessModal: toggleSuccessModal },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(DocumentTableView);